import { SalesOrderDataRetrive } from "Common/CommonEnquiryData/SalesOrderServiceType";
import { SALES_ORDER_TYPE } from "Constants/StatusConstant";
import { SALES_ORDER_CATEGORY } from "Constants/StatusConstant";
import { parse } from "date-fns";
import { convertLocalDateToUTC } from "helpers/DateUtils/dateTimezoneUtils";

export const buildSalesOrderPayload = (
  formData: SalesOrderDataRetrive,
  items: any[],
  // type: "product" | "service" = "service",
  action: "draft" | "save" | "print" = "save",
  shippingAddresses?: {
    id: string;
    address: string;
    state?: string;
    city?: string;
    pin_code?: string;
    country?: string;
    phone_number?: string;
  }[],
  organizationsState?: any[],
  isEdit: boolean = false
) => {
  // Helper to convert date string to UTC ISO string
  // Handles yyyy-MMM-dd format (e.g., "2026-Jan-14") and YYYY-MM-DD format
  // Converts local date to UTC ISO string for backend
  const convertToUTC = (dateStr: string | Date | null | undefined): string => {
    if (!dateStr) return "";

    // If it's a Date object, convert directly
    if (dateStr instanceof Date) {
      if (isNaN(dateStr.getTime())) return "";
      return dateStr.toISOString();
    }

    // If it's a string
    if (typeof dateStr === "string") {
      // If already UTC ISO string, return as is
      if (dateStr.includes("T") || dateStr.includes("Z")) {
        return dateStr;
      }

      // If empty string or invalid, return empty
      if (dateStr.trim() === "" || dateStr === "Invalid Date") return "";

      try {
        let localDateStr = "";

        // Try parsing yyyy-MMM-dd format first (e.g., "2026-Jan-14")
        let parsed = parse(dateStr, "yyyy-MMM-dd", new Date());
        if (!isNaN(parsed.getTime())) {
          // Convert to YYYY-MM-DD format
          const year = parsed.getFullYear();
          const month = String(parsed.getMonth() + 1).padStart(2, "0");
          const day = String(parsed.getDate()).padStart(2, "0");
          localDateStr = `${year}-${month}-${day}`;
        }
        // Try parsing YYYY-MM-DD format (e.g., "2026-01-14")
        else if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          localDateStr = dateStr;
        }

        // Convert local date string to UTC ISO string
        if (localDateStr) {
          return convertLocalDateToUTC(localDateStr);
        }

        return "";
      } catch (error) {
        return "";
      }
    }

    return "";
  };

  // Retention details (optional)
  const retention = (formData as any)?.retention_details || {};
  const retention_details = {
    retention_applicable: !!(retention?.retention_applicable ?? retention?.is_applicable),
    retention_type: retention?.retentionType || retention?.retention_type || "",
    retention_percentage:
      retention?.percentage === "" || retention?.percentage === null || retention?.percentage === undefined
        ? ""
        : Number(retention?.percentage),
    retention_amount:
      retention?.retentionAmount === "" || retention?.retentionAmount === null || retention?.retentionAmount === undefined
        ? ""
        : Number(retention?.retentionAmount),
    next_follow_up_date: convertToUTC(retention?.followUpDate || retention?.next_follow_up_date || retention?.follow_up_date),
  };

  // Get company data
  const company = formData?.company || {};
  const companyId = company?.id || company?.company_id || "";
  const companyName = formData?.company_name || company?.company_name || "";
  const companyEmail = formData?.company_email || "";
  const vendorCode = company?.vendor_code || (formData as any)?.vendor_code || "";
  const branchName = company?.branch_name || (formData as any)?.branch_name || "";
  // const industry = company?.industry || formData?.industry || "";

  // Get company details from the original company object
  const companyData = company;

  // Get billing address
  const billingAddr = formData?.billing_address || {};
  const billingAddress = {
    city: billingAddr?.city || "",
    state: billingAddr?.state || "",
    address: billingAddr?.address || "",
    country: billingAddr?.country || "",
    pin_code: billingAddr?.pin_code || "",
    phone_number: billingAddr?.phone_number || "",
  };

  // Transform contact details
  const contactDetails = (formData?.contactPersonArray || company?.contact_details || []).map((contact: any) => ({
    contact_id: contact?.contact_id || contact?.id || "",
    company_id: companyId,
    name: contact?.contact_person_name || contact?.name || "",
    designation: contact?.contact_person_designation || contact?.designation || "",
    email: contact?.contact_person_email || contact?.email || "",
    phone_number: contact?.contact_person_phone_no || contact?.phone_number || "",
  }));

  // Get shipping addresses
  const shippingAddress = (shippingAddresses || []).map((addr: any) => ({
    shipping_address_id: addr?.id || addr?.shipping_address_id || "",
    address: addr?.address || "",
    city: addr?.city || "",
    state: addr?.state || "",
    country: addr?.country || "",
    pin_code: addr?.pin_code || addr?.pincode || "",
    phone_number: addr?.phone_number || "",
  }));

  // Get bill_from (organization) - full object
  let billFrom = null;
  if (formData?.bill_from_address) {
    if (typeof formData.bill_from_address === "object" && formData.bill_from_address !== null) {
      const billFromAddr = formData.bill_from_address as any;
      if (billFromAddr.organization_id) {
        // Full organization object
        billFrom = {
          ...billFromAddr
          //   organization_id: billFromAddr.organization_id || "",
          //   organization_name: billFromAddr.organization_name || billFromAddr.org_name || "",
          //   org_address: billFromAddr.org_address || billFromAddr.address || "",
          //   industry_type: billFromAddr.industry_type || "",
          //   org_city: billFromAddr.org_city || billFromAddr.city || "",
          //   org_state: billFromAddr.org_state || billFromAddr.state || "",
          //   org_country: billFromAddr.org_country || billFromAddr.country || "",
          //   org_pincode: billFromAddr.org_pincode || billFromAddr.pin_code || billFromAddr.pincode || "",
          //   org_status: billFromAddr.org_status || false,
          //   is_active: billFromAddr.is_active !== undefined ? billFromAddr.is_active : true,
          //   is_deleted: billFromAddr.is_deleted !== undefined ? billFromAddr.is_deleted : false,
          //   created_at: billFromAddr.created_at || "",
          //   updated_at: billFromAddr.updated_at || "",
          //   created_by: billFromAddr.created_by || "",
          //   updated_by: billFromAddr.updated_by || "",
        };
      } else {
        // Simple address object
        billFrom = {
          city: billFromAddr.city || "",
          state: billFromAddr.state || "",
          address: billFromAddr.address || billFromAddr.org_address || "",
          country: billFromAddr.country || "",
          pincode: billFromAddr.pin_code || billFromAddr.pincode || "",
          phone_number: billFromAddr.phone_number || "",
        };
      }
    } else if (organizationsState && organizationsState.length > 0) {
      // Find organization from state
      const orgObj = organizationsState.find(
        (org: any) => org.org_address === formData.bill_from_address || org.organization_id === formData.bill_from_address
      );
      if (orgObj) {
        billFrom = {
          ...orgObj
          //   organization_id: orgObj.organization_id || "",
          //   organization_name: orgObj.organization_name || "",
          //   org_address: orgObj.org_address || "",
          //   industry_type: orgObj.industry_type || "",
          //   org_city: orgObj.org_city || orgObj.city || "",
          //   org_state: orgObj.org_state || orgObj.state || "",
          //   org_country: orgObj.org_country || orgObj.country || "",
          //   org_pincode: orgObj.org_pincode || orgObj.pin_code || orgObj.pincode || "",
          //   org_status: orgObj.org_status || false,
          //   is_active: orgObj.is_active !== undefined ? orgObj.is_active : true,
          //   is_deleted: orgObj.is_deleted !== undefined ? orgObj.is_deleted : false,
          //   created_at: orgObj.created_at || "",
          //   updated_at: orgObj.updated_at || "",
          //   created_by: orgObj.created_by || "",
          //   updated_by: orgObj.updated_by || "",
        };
      }
    }
  }

  // Get bill_to (billing address)
  const billTo = billingAddress;

  // Get agent name
  const agentName = formData?.agent_name && formData.agent_name.trim() !== "" ? formData.agent_name : null;

  // Get assigned members (full user objects)
  // Schema for Service expects `assigned_technician` as Json?
  // But also usually `assigned_members` for general members?
  // Looking at schema: `assigned_technician` is there.
  // There is no explicit `assigned_members` column in the provided `SalesOrderService` schema, 
  // but there is `initiated_by`. 
  // However, usually "Members" in the form implies people working on it.
  // The schema provided has `assigned_technician`.
  // Wait, let's check `User` relation? 
  // Schema: `created_by_user`, `updated_by_user`. 
  // It seems `assigned_technician` handles the technicians.
  // What about "Members" selected in the form? 
  // The form has "Assign Members" AND "Add Technicians".
  // "Assign Members" usually maps to `assigned_members` in other tables. 
  // In `SalesOrderService` schema provided:
  // `assigned_technician` Json?
  // There is NO `assigned_members` column in the schema snippet provided in Step 1.
  // But typically it might be stored in a Json field if not relational.
  // Let's assume "Members" go into a generic field or maybe they are NOT stored?
  // Wait, the Product schema usually has it.
  // The form sends `members`. 
  // In the original Payload builder (product), it sent `assigned_members` inside `sales_order_details`.
  // I will include it. If the backend doesn't take it, it might just be ignored or I should put it in `assigned_technician` if that's what it means? 
  // No, `technicians` are separate.
  // Let's check `SalesOrderService` schema again.
  // `assigned_technician` Json?
  // `initiated_by` String
  // `customer_details` Json
  // Maybe `assigned_members` is missing from the provided schema snippet or I missed it?
  // No, I see `assigned_technician`.
  // I will assign `technicians` from form to `assigned_technician`.
  // I will assign `members` from form to `assigned_members` (following product pattern) but if it's not in schema it might fail validation if backend uses strict casting.
  // However, typically `assigned_technician` is for service people.

  // Technicians from form
  const technicians = (formData?.technicians || []).map((t: any) => ({
    user_id: t?.user_id || t?.id || "",
    username: t?.username || "",
    first_name: t?.first_name || "",
    last_name: t?.last_name || "",
    full_name: t?.first_name && t?.last_name
      ? `${t.first_name} ${t.last_name}`.trim()
      : t?.full_name || t?.fullname || "",
  }));

  // Members from form
  const assignedMembers = (formData?.members || []).map((m: any) => ({
    user_id: m?.user_id || m?.id || m?.user || "",
    username: m?.username || "",
    first_name: m?.first_name || "",
    last_name: m?.last_name || "",
    email: m?.email || m?.user_email || "",
    designation: m?.designation || "",
    address: m?.address || "",
    city: m?.city || "",
    state: m?.state || "",
    country: m?.country || "",
    pin_code: m?.pin_code || m?.pincode || "",
    mobile_number: m?.mobile_number || m?.phone_number || "",
    user_type: m?.user_type || "",
  }));


  // Get currency details
  // const currencyCode = formData?.currency_code || "INR";
  // const currencySymbol = formData?.currency_symbol || "";
  // const currencyRate = formData?.exchange_rate || "";

  // Get EMD details (if available in formData)
  // Send null instead of empty string when not provided (backend expects string or null)
  const modeOfEmd = (formData as any)?.mode_of_emd && String((formData as any).mode_of_emd).trim() !== ""
    ? String((formData as any).mode_of_emd).trim()
    : null;
  const emd = (formData as any)?.emd || "";
  // Handle emd_amount: check if it exists and is not empty string (but allow 0 as valid value)
  const emdAmountValue = (formData as any)?.emd_amount;
  const emdAmount = emdAmountValue !== null && emdAmountValue !== undefined && emdAmountValue !== "" && String(emdAmountValue).trim() !== ""
    ? emdAmountValue
    : null;

  // Get inspection/installation
  const inspection = formData?.inspection && formData.inspection.trim() !== "" ? formData.inspection : null;
  const inspectionDate = convertToUTC(formData?.inspection_date);
  const installation = formData?.installation && formData.installation.trim() !== "" ? formData.installation : null;
  const installationDate = convertToUTC(formData?.installation_date);

  // Get PBG details
  const pbgDetails = formData?.pbg_details;
  const pbgApplicable = pbgDetails?.pbg_required || false;
  const pbgNumber = pbgDetails?.pbg_number || "";
  const pbgDate = convertToUTC(pbgDetails?.pbg_date);
  const bankName = pbgDetails?.bank_name || "";
  const pbgAmount = Number(pbgDetails?.amount || 0);
  // Handle pbg_validity: if it's a valid date, convert to UTC; otherwise send null
  // The form allows text input like "12 months", but backend expects DateTime or null
  // Match the logic from SalesOrderProductPayloadType.tsx
  let pbgValidityFormatted: string | null = null;
  const pbgValidityConverted = convertToUTC(pbgDetails?.validity);
  if (pbgValidityConverted && pbgValidityConverted.trim() !== "") {
    // convertToUTC returned a valid date string
    pbgValidityFormatted = pbgValidityConverted;
  } else {
    // Try to parse as Date to check if it's a valid date
    const dateTry = new Date(pbgDetails?.validity || "");
    if (!isNaN(dateTry.getTime())) {
      pbgValidityFormatted = dateTry.toISOString();
    } else {
      // Not a valid date (e.g., "12 months"), send null instead of empty string
      pbgValidityFormatted = null;
    }
  }

  const bgStatus = pbgDetails?.pbg_status || "";
  // Store raw pbg_documents for file extraction
  const pbgDocumentsRaw = pbgDetails?.upload_document || [];

  // Get terms and conditions - transform from form format (description with newlines) to backend format (array of {type, options})
  let termsAndConditions: any[] = [];
  if (formData?.terms_and_conditions) {
    if (typeof formData.terms_and_conditions === "string") {
      // Try to parse if it's JSON string
      try {
        const parsed = JSON.parse(formData.terms_and_conditions);
        termsAndConditions = Array.isArray(parsed) ? parsed : [];
      } catch {
        // If not JSON, create array with single entry
        termsAndConditions = [{ type: "other", options: formData.terms_and_conditions }];
      }
    } else if (Array.isArray(formData.terms_and_conditions)) {
      // Transform each term: if description has newlines, split into multiple entries
      termsAndConditions = formData.terms_and_conditions.flatMap((term: any) => {
        if (!term.type) return [];

        // If description exists and contains newlines, split into multiple options
        if (term.description && term.description.includes("\n")) {
          const options = term.description.split("\n").filter((opt: string) => opt.trim() !== "");
          // Create one entry per option with the same type
          return options.map((option: string) => ({
            type: term.type,
            options: option.trim(),
          }));
        } else {
          // Single option - use description or option field
          const option = term.description || term.option || "";
          if (option.trim() === "") return [];
          return [{
            type: term.type,
            options: option.trim(),
          }];
        }
      });
    }
  }

  // Get totals
  const subtotalBeforeDiscount = formData?.subtotal_before_discount || "0";
  const totalItemDiscounts = formData?.total_item_discounts || "0";
  const totalGstValue = formData?.total_gst || "0";
  const netTotal = formData?.net_taxable_value || "0";
  const grandTotalAmount = formData?.total_amount || "0";

  // Get customer PO details
  const customerPoNumber = (formData as any)?.customer_po_number || "";
  const customerPoDate = convertToUTC((formData as any)?.customer_po_date);

  // Process all documents first
  const customerPoDocumentsArray = Array.isArray(formData?.customer_po_document_url) ? formData.customer_po_document_url : [];
  const customerPoDocumentFiles: File[] = customerPoDocumentsArray.filter((doc: any) => doc instanceof File) as File[];
  const customerPoDocumentUrls: string[] = customerPoDocumentsArray.filter((doc: any) => typeof doc === "string") as string[];

  // Build customer_details
  // Try to get gstin and gst_type from company object or formData
  const gstin = (companyData as any)?.gstin || (formData as any)?.gstin || "";
  const gstType = (companyData as any)?.gst_type || (formData as any)?.gst_type || "";
  const companyPhoneNumber = (companyData as any)?.phone_number || (formData as any)?.phone_number || formData?.contact_person_phone_no || "";

  const customer_details = {
    gstin: gstin,
    gst_type: gstType,
    industry: (companyData as any)?.industry || (formData as any)?.industry || "",
    company_id: companyId,
    company_name: companyName,
    vendor_code: vendorCode,
    branch_name: branchName,
    sr_no: (companyData as any)?.sr_no || (formData as any)?.sr_no || 1,
    phone_number: companyPhoneNumber,
    email: companyEmail,
    address: billingAddress.address || `${billingAddress.city}, ${billingAddress.state}, ${billingAddress.country}`,
    contact_details: contactDetails,
    billing_address: billingAddress,
    shipping_address: shippingAddress,
    customer_po_number: customerPoNumber,
    customer_po_date: customerPoDate,
    customer_po_documents: customerPoDocumentUrls, // Add customer PO documents
  };

  // Build sales_order_service_items
  const sales_order_service_items = items.map((item: any) => {
    // Basic fields
    const productId = item?.item?.id || item?.item || item?.product_id || "";
    const productName = item?.item_name || item?.product_name || item?.name || "";
    const categoryName =
      item?.category_name ||
      item?.category ||
      item?.serviceCategory ||
      item?.service_category ||
      item?.category?.category_name ||
      "";
    const subCategoryName =
      item?.sub_category_name ||
      item?.sub_category ||
      item?.serviceType ||
      item?.service_type ||
      item?.sub_category?.sub_category_name ||
      "";
    const makeName = item?.make_name || item?.make?.make_name || "";
    const modelName = item?.model_name || item?.model?.model_name || "";
    const unitName =
      item?.unit_name ||
      item?.unit ||
      item?.unit?.unit_name ||
      "Nos";

    // Product Type (JSON)
    let productTypeArray: any[] = [];
    if (Array.isArray(item.product_type)) {
      productTypeArray = item.product_type;
    } else if (item?.type) {
      productTypeArray = Array.isArray(item.type) ? item.type : [item.type];
    }

    // Shipping address for item
    let itemShippingAddress: any = {};
    if (item?.shipping_address_id && shippingAddresses && shippingAddresses.length > 0) {
      const shippingAddr = shippingAddresses.find(
        (addr) => addr.id === item.shipping_address_id
      );
      if (shippingAddr) {
        itemShippingAddress = {
          shipping_address_id: shippingAddr.id || "",
          address: shippingAddr.address || "",
          city: shippingAddr.city || "",
          state: shippingAddr.state || "",
          country: shippingAddr.country || "",
          pin_code: shippingAddr.pin_code || "",
          phone_number: shippingAddr.phone_number || "",
        };
      }
    } else if (item?.shipping_address && typeof item.shipping_address === "object") {
      // If shipping_address is populated in item
      const sa = item.shipping_address;
      itemShippingAddress = {
        shipping_address_id: sa.id || sa.shipping_address_id || "",
        address: sa.address || "",
        city: sa.city || "",
        state: sa.state || "",
        country: sa.country || "",
        pin_code: sa.pin_code || sa.pincode || "",
        phone_number: sa.phone_number || "",
      }
    }


    // Calculation fields
    const quantity = Number(item?.productQuantity || item?.quantity || item?.product_quantity || 1);
    const unitPrice = Number(item?.unit_price || item?.serviceRate || 0);
    const discountType = item?.discount_type || "none";

    // Handle discount_value based on discount_type
    let discountValue: number;
    if (discountType === "none") {
      discountValue = 0;
    } else if (discountType === "%" || discountType === "percentage") {
      // For percentage, use discount_percent if available, otherwise discount_value
      discountValue = Number(item?.discount_percent ?? item?.discount_value ?? 0);
    } else {
      // For currency/flat discount, use discount_value directly (don't fall back to discount_percent)
      discountValue = Number(item?.discount_value ?? 0);
    }

    // Get discount_percent - only include when discount_type is "%"
    const discountPercent = discountType === "%" || discountType === "percentage"
      ? (item?.discount_percent !== undefined && item?.discount_percent !== null
        ? Number(item.discount_percent)
        : (item?.discount !== undefined && item?.discount !== null
          ? Number(item.discount)
          : null))
      : null;

    // Mapped tax fields
    const sacCode = item?.sac_code || item?.hsn_code;
    const gstPercentage = Number(item?.gst_percent || item?.gst_percentage || 0);
    const igstPercentage = Number(item?.igst_percent || item?.igst_percentage || 0);
    const cgstPercentage = Number(item?.cgst_percent || item?.cgst_percentage || 0);
    const sgstPercentage = Number(item?.sgst_percent || item?.sgst_percentage || 0);
    const gstValue = Number(item?.gst_amount || item?.gst_value || 0);

    const remark = item?.remark || item?.description || "";

    // Totals
    // total_service_amount should be the final total (net_value + gst_value), same as total_price
    const totalServiceAmount = Number(
      item?.total_price ||
      item?.product_total ||
      item?.total_service_amount ||
      item?.total_amount ||
      item?.totalServiceAmount ||
      0
    );
    const totalServiceNetAmount = Number(
      item?.net_value ||
      item?.total_service_net_amount ||
      item?.product_net_total ||
      0
    );

    return {
      sales_order_service_item_id: item?.sales_order_service_item_id || null,
      quotation_service_item_id: item?.quotation_service_item_id || null,
      product_id: productId,
      product_name: productName,
      category_name: categoryName,
      sub_category_name: subCategoryName,

      serial_number: item?.serialNo || item?.serial_number || item?.serial_no || null,
      product_type: productTypeArray,

      make_name: makeName,
      model_name: modelName,
      unit_name: unitName,
      unit_price: unitPrice,

      sac_code: sacCode,
      gst_percentage: gstPercentage,
      igst_percentage: igstPercentage,
      cgst_percentage: cgstPercentage,
      sgst_percentage: sgstPercentage,
      gst_value: gstValue,

      remark: remark,
      product_sku: item?.product_sku || item?.sku || "",

      product_quantity: quantity,

      total_service_amount: totalServiceAmount,
      total_service_net_amount: totalServiceNetAmount,

      discount_type: discountType,
      discount_percent: discountPercent,
      discount_value: discountValue,

      prod_description: item?.prod_description ?? item?.description ?? null,
      additional_details: item?.additional_details ?? null,

      is_active: item?.is_active ?? true,
      serviceable: item?.is_serviceable !== undefined ? item.is_serviceable : (item?.serviceable !== undefined ? item.serviceable : false),

      shipping_address: itemShippingAddress,

      // Additional Service Fields (from schema)
      service_category: item?.serviceCategory || item?.service_category || categoryName || null,
      service_method: item?.serviceMethod || item?.service_method || null,
      service_type: item?.serviceType || item?.service_type || null,
      service_rate: item?.serviceRate ? Number(item.serviceRate) : null,
      execution_mode: item?.executionMode || null,
      reported_complaints: item?.reportedComplaint || null,

      // Warranty fields (if applicable for services)
      warranty_in_months: item?.warranty_in_months && item.warranty_in_months.toString().trim() !== "" ? item.warranty_in_months.toString() : null,
      warranty_date: item?.warranty_date && item.warranty_date.toString().trim() !== "" ? convertToUTC(item.warranty_date) : null,

      // Financial fields
      gross_value: item?.gross_value ? String(item.gross_value) : null,
      rate_in_currency: item?.rate_in_currency || item?.rate_in ? String(item.rate_in_currency || item.rate_in) : null,

      is_deleted: item?.is_deleted ?? false,
      accessories: Array.isArray(item?.accessories)
        ? item.accessories.map((acc: any) => {
          const discountTypeRaw = (acc.discount_type || "").toString();
          const isPercentage =
            discountTypeRaw === "%" ||
            discountTypeRaw === "percentage" ||
            discountTypeRaw.toLowerCase() === "percent";

          return {
            ...(isEdit && acc.accessory_id
              ? { accessory_id: acc.accessory_id }
              : {}),
            product_id: acc.product_id,
            quantity: Number(acc.quantity || 0),
            unit_price: Number(acc.unit_price || 0),
            discount_type: isPercentage ? "%" : "Flat",
            discount_value: Number(acc.discount_value || 0),
            gst_percentage: Number(acc.gst_percentage || 0),
            cgst_percentage: Number(acc.cgst_percentage || 0),
            sgst_percentage: Number(acc.sgst_percentage || 0),
            igst_percentage: Number(acc.igst_percentage || 0),
            remark: acc.remark || null,
          };
        })
        : [],
      //   created_by: item?.created_by || null, 
      //   updated_by: item?.updated_by || null,
    };
  });

  // Extract File objects
  const attachmentsArray = Array.isArray(formData?.attachments) ? formData.attachments : [];
  const attachmentFiles: File[] = attachmentsArray.filter((att: any) => att instanceof File) as File[];
  const attachmentUrls: string[] = attachmentsArray.filter((att: any) => typeof att === "string") as string[];

  const pbgDocumentsArray = Array.isArray(pbgDocumentsRaw) ? pbgDocumentsRaw : [];
  const pbgDocumentFiles: File[] = pbgDocumentsArray.filter((doc: any) => doc instanceof File) as File[];
  const pbgDocumentUrls: string[] = pbgDocumentsArray.filter((doc: any) => typeof doc === "string") as string[];

  // Collect all follow-ups
  const followUps: any[] = [];

  // Add retention follow-up only if retention is applicable and follow-up date is filled
  const retentionFollowUpDate = retention_details?.next_follow_up_date;
  if (
    retention_details?.retention_applicable &&
    retentionFollowUpDate &&
    retentionFollowUpDate.trim()
  ) {
    followUps.push({
      next_follow_up_date: convertToUTC(retentionFollowUpDate),
      follow_up_type: "Initial Follow-up",
      follow_up_category: SALES_ORDER_CATEGORY?.RETENTION,
      follow_up_remarks: "",
      source_entity_type: SALES_ORDER_TYPE.SERVICE,
    });
  }

  // Add PBG follow-up only if PBG is applicable and follow-up date is filled
  const pbgFollowUpDate = (formData as any)?.pbg_details?.next_follow_up_date;
  if (pbgApplicable && pbgFollowUpDate && pbgFollowUpDate.trim()) {
    followUps.push({
      next_follow_up_date: convertToUTC(pbgFollowUpDate),
      follow_up_type: "Initial Follow-up",
      follow_up_category: SALES_ORDER_CATEGORY?.PBG,
      source_entity_type: SALES_ORDER_TYPE.SERVICE,
      follow_up_remarks: "",
    });
  }

  // Add general follow-up from ScheduleFollowUp component only if not in edit mode
  // In edit forms, "Other" follow-up is not needed
  if (!isEdit && (formData as any)?.next_follow_up_date && (formData as any)?.next_follow_up_date.trim()) {
    followUps.push({
      next_follow_up_date: convertToUTC((formData as any)?.next_follow_up_date),
      follow_up_type: "Initial Follow-up",
      follow_up_category: SALES_ORDER_CATEGORY?.OTHERS,
      source_entity_type: SALES_ORDER_TYPE.SERVICE,
      follow_up_remarks: String((formData as any)?.follow_up_remarks || ""),
    });
  }

  // --- Final Payload Construction for Service ---
  const payload = {
    // Map to SalesOrderService Root Fields
    srv_date: convertToUTC(formData?.srv_date),
    schedule_service_date: convertToUTC(formData?.schedule_service_date),
    initiated_by: formData?.initiated_by || "",
    reference_number:
      formData?.reference_number && String(formData.reference_number).trim() !== ""
        ? String(formData.reference_number).trim()
        : null,

    assigned_members: assignedMembers,
    assigned_technician: technicians, // Json field

    quotation_service_id: formData?.quotation !== undefined && formData?.quotation !== null && formData?.quotation !== "" ? formData.quotation : null,
    quotation_number: formData?.quotation_number !== undefined && formData?.quotation_number !== null && formData?.quotation_number !== "" ? formData.quotation_number : null,

    enquiry_service_id: formData?.enquiry !== undefined && formData?.enquiry !== null && formData?.enquiry !== "" ? formData.enquiry : null,
    enquiry_number: formData?.enquiry_number !== undefined && formData?.enquiry_number !== null && formData?.enquiry_number !== "" ? formData.enquiry_number : null,
    uid_number: formData?.uid_number !== undefined && formData?.uid_number !== null && formData?.uid_number !== "" ? formData.uid_number : null,

    sales_order_stage: action === "draft" ? "draft" : "orders_log",
    sales_order_status: formData?.status && formData.status.trim() !== "" ? formData.status : null, // "In Progress" etc

    customer_details: customer_details,
    customer_name: companyName, // Denormalized
    agent_name: agentName,

    // Currency & Exchange
    currency_applicable: !!(formData as any)?.currency_applicable,
    currency_code:
      (formData as any)?.currency_code && String((formData as any).currency_code).trim() !== ""
        ? String((formData as any).currency_code).trim()
        : "INR",
    currency_symbol:
      (formData as any)?.currency_symbol && String((formData as any).currency_symbol).trim() !== ""
        ? String((formData as any).currency_symbol).trim()
        : "₹",
    exchange_rate: (() => {
      const v = (formData as any)?.exchange_rate;
      if (v === null || v === undefined || String(v).trim() === "") return null;
      const n = typeof v === "string" ? parseFloat(v) : v;
      return Number.isFinite(n) ? n : null;
    })(),
    composition_gst: !!(formData as any)?.composition_gst,
    select_composition_gst:
      (formData as any)?.select_composition_gst && String((formData as any).select_composition_gst).trim() !== ""
        ? String((formData as any).select_composition_gst).trim()
        : null,

    emd_applicable: !!(formData as any)?.emd_applicable,
    mode_of_emd: modeOfEmd, // null if not provided or empty
    emd_amount: emdAmount !== null && emdAmount !== undefined && emdAmount !== ""
      ? Number(emdAmount)
      : null,
    payment_terms_amount: (() => {
      const val = (formData as any)?.payment_terms_amount;
      if (val === null || val === undefined || val === "" || String(val).trim() === "") return null;
      const num = typeof val === "string" ? parseFloat(val) : val;
      return Number.isFinite(num) ? num : null;
    })(),

    inspection_applicable: !!(formData as any)?.inspection_applicable,
    inspection: inspection,
    inspection_date: inspectionDate && inspectionDate.trim() !== "" ? inspectionDate : null,
    installation: installation,
    installation_date: installationDate && installationDate.trim() !== "" ? installationDate : null,

    pbg_applicable: pbgApplicable,
    pbg_number: pbgNumber || null,
    pbg_date: pbgDate ? pbgDate : null,
    bank_name: bankName || null,
    pbg_amount: pbgAmount || null,
    pbg_validity: pbgValidityFormatted, // mapped null if invalid
    pbg_status: bgStatus || null,
    pbg_documents: pbgDocumentUrls, // URL string array

    bill_to: billTo,
    bill_from: billFrom,

    notes: formData?.notes && formData.notes.trim() !== "" ? formData.notes : null,
    attachment_description: (formData as any)?.attachment_description && String((formData as any).attachment_description).trim() !== ""
      ? String((formData as any).attachment_description).trim()
      : null,
    attachments: attachmentUrls,

    terms_and_conditions: termsAndConditions,

    retention_details: retention_details,

    // Totals
    sub_total_before_discount: Number(subtotalBeforeDiscount),
    total_discount_value: Number(totalItemDiscounts),
    total_gst_value: Number(totalGstValue),
    total_net_value: Number(netTotal),
    grand_total_amount: Number(grandTotalAmount),

    // Items
    sales_order_service_items: sales_order_service_items,

    // Follow Ups
    sales_order_follow_ups: followUps,

    // Tags (ensure array of strings, lowercased and trimmed, unique)
    tags: Array.isArray((formData as any)?.tags)
      ? Array.from(
        new Set(
          ((formData as any).tags as any[])
            .map((t) => String(t).trim())
            .filter((t) => t.length > 0)
        )
      )
      : [],

    // Action
    action: action,

    // File Objects for FormData
    _attachmentFiles: attachmentFiles,
    _pbgDocumentFiles: pbgDocumentFiles,
    _customerPoDocumentFiles: customerPoDocumentFiles,
  };

  return payload;
};
